/**
 * Analysis Error Badge Component
 * Shown when analysis failed but the conversation response was delivered fine.
 * Styled like FeedbackBadge but with error colors and a retry action.
 */

import React from 'react';
import { TouchableOpacity } from 'react-native';
import { Box, HStack, Text } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { FS_XS } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

interface AnalysisErrorBadgeProps {
  onRetry: () => void;
  disabled?: boolean;
}

export const AnalysisErrorBadge = React.memo(function AnalysisErrorBadge({
  onRetry,
  disabled,
}: AnalysisErrorBadgeProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  return (
    <TouchableOpacity
      testID="chat-button-retry-analysis"
      accessibilityLabel={t('chat.messages.retryAnalysis')}
      accessibilityHint={t('chat.messages.retryAnalysisHint')}
      accessibilityRole="button"
      onPress={onRetry}
      disabled={disabled}
      style={disabled ? { opacity: 0.5 } : undefined}
    >
      <Box
        style={{ backgroundColor: theme.errorBg, borderColor: theme.error }}
        borderWidth={1}
        rounded="$full"
        px="$3"
        py="$1"
        alignSelf="flex-end"
        hardShadow="1"
      >
        <HStack space="xs" alignItems="center">
          <Text
            allowFontScaling={false}
            style={{
              fontSize: FS_XS * fontScale,
              color: theme.error,
              fontWeight: '600',
              ...ffs,
            }}
          >
            {t('chat.messages.analysisUnavailable')}
          </Text>
          <Text
            allowFontScaling={false}
            style={{
              fontSize: FS_XS * fontScale,
              color: theme.error,
              fontWeight: '600',
              ...ffs,
            }}
          >
            {t('chat.messages.tapToRetryAnalysis')}
          </Text>
        </HStack>
      </Box>
    </TouchableOpacity>
  );
});
